import { t, defineMessage } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Slider, Typography } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { onChangeChainStatsMinCount, onChangeChainStatsMinTotalTrips, onChangeChainStatsSizeMetric, onClearChainStatsSelection } from '../../../ac/onChangeMapFilters';
import {
    CHAIN_STATS_COLOR_LOW, CHAIN_STATS_COLOR_MID_LOW, CHAIN_STATS_COLOR_MID_HIGH, CHAIN_STATS_COLOR_HIGH,
    CHAIN_STATS_NODE_COLOR_STOPS, CHAIN_STATS_EDGE_COLOR_STOPS, CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS,
} from '../../../constants/layers';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { GradientLegend } from '../../LegendRenderer/types/GradientLegend';
import { StandardRadioGroup } from '../StandardRadioGroup';
import styles from '../FilterSidebar.module.css';

// rebalance-chains — легенда слоя «Статистика цепочек трипов»:
//  - переключатель median/max (chainStatsSizeMetric) — по какой глубине цепочки взвешивать
//    размер точки на уровне 0 И фильтровать слайдером ниже (оба завязаны на одно поле —
//    median_chain_length либо max_chain_length, см. CHAIN_STATS_RADIUS_BY_MEDIAN/_BY_MAX
//    в constants/layers.js и setChainStatsView в Map.jsx);
//  - слайдер «Длина цепочки не менее» (фильтр по выбранному выше полю), по образцу
//    слайдера «Число перевозок не менее» у слоя «Фактический ребаланс»;
//  - слайдер «Число поездок через парковку не менее» (фильтр по total_trips, только
//    уровень 0 — распределение сильно скошено, поэтому шкала нелинейная: индексный Slider
//    по CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS, см. constants/layers.js — MUI v4 не умеет
//    логарифмический scale сам);
//  - max/median для парковки открытой цепочки (если она есть);
//  - цветовая шкала по числу поездок (мало → много: синий → зелёный → оранжевый → красный),
//    см. CHAIN_STATS_NODE_COLOR/CHAIN_STATS_EDGE_COLOR в constants/layers.js. У парковок и
//    рёбер разный масштаб значений (total_trips vs trip_count) — две отдельные шкалы;
//    шкала рёбер показана только при открытой цепочке (иначе рёбра не видны на карте).
// Отступы — по образцу ColoredItemsLegend/LegendRenderer: заголовки (.legHead) сами себя
// паддят на 10px, доп. paddingLeft/Right вешаем только на строки БЕЗ .legHead (иначе
// заголовок получает двойной отступ — 10px обёртки поверх 10px из самого класса).
const CHAIN_STATS_SIZE_METRIC_OPTIONS = [
    { value: 'median', label: defineMessage({ message: 'Медианная' }) },
    { value: 'max', label: defineMessage({ message: 'Максимальная' }) },
];
const CHAIN_STATS_COLOR_ITEMS = [
    CHAIN_STATS_COLOR_LOW, CHAIN_STATS_COLOR_MID_LOW, CHAIN_STATS_COLOR_MID_HIGH, CHAIN_STATS_COLOR_HIGH,
];
const buildChainStatsColorLegendItems = (stops, highSuffix) => stops.map((value, idx) => ({
    id: `chain-stats-color-${idx}`,
    label: idx === stops.length - 1 ? `${value}${highSuffix}` : `${value}`,
    color: CHAIN_STATS_COLOR_ITEMS[idx],
}));
const CHAIN_STATS_NODE_COLOR_ITEMS = buildChainStatsColorLegendItems(CHAIN_STATS_NODE_COLOR_STOPS, '+');
const CHAIN_STATS_EDGE_COLOR_ITEMS = buildChainStatsColorLegendItems(CHAIN_STATS_EDGE_COLOR_STOPS, '+');
const CHAIN_STATS_GRADIENT_RENDER_CONFIG = { gradientHeight: '16px' };
const ChainStatisticsLegendComponent = ({
    chainStatsMinCount,
    onChangeChainStatsMinCount,
    chainStatsSizeMetric,
    onChangeChainStatsSizeMetric,
    chainStatsMinTotalTrips,
    onChangeChainStatsMinTotalTrips,
    chainStatsSelectedParkingId,
    chainStatsSelectedParkingStats,
    onClearChainStatsSelection,
}) => (
    <div style={{ marginTop: 10 }}>
        {chainStatsSelectedParkingId !== null && typeof chainStatsSelectedParkingId !== 'undefined' && (
            <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                <button
                    type="button"
                    className={styles.scrBackRow}
                    onClick={onClearChainStatsSelection}
                    title={t`Назад ко всем парковкам`}
                >
                    ‹ {t`Назад ко всем парковкам`}
                </button>
            </div>
        )}
        <div className={styles.legHead}>{t`Глубина цепочки для размера точки`}:</div>
        <StandardRadioGroup
            value={chainStatsSizeMetric}
            onChange={({ target: { value } }) => onChangeChainStatsSizeMetric(value)}
        >
            {CHAIN_STATS_SIZE_METRIC_OPTIONS.map(({ value, label }) => (
                <FormControlLabel
                    key={value}
                    className={styles.isochroneFilterLabel}
                    control={
                        <Radio
                            checked={chainStatsSizeMetric === value}
                            value={value}
                            className={classnames(styles.isochroneFilterInput, {
                                [styles.checkedIsochroneFilterInput]: chainStatsSizeMetric === value,
                            })}
                            size="small"
                        />
                    }
                    label={i18n._(label)}
                    labelPlacement="end"
                />
            ))}
        </StandardRadioGroup>
        <div className={styles.legHead}>{t`Длина цепочки не менее:`}</div>
        <div style={{ paddingLeft: 10, paddingRight: 10 }}>
            <Typography
                gutterBottom
                style={{ marginTop: 4 }}
            >
                <strong>{chainStatsMinCount}</strong>
            </Typography>
            <Slider
                value={chainStatsMinCount}
                min={0}
                max={15}
                step={1}
                marks={[ 0, 3, 6, 9, 12, 15 ].map(v => ({ value: v }))}
                valueLabelDisplay="off"
                onChange={(_, v) => onChangeChainStatsMinCount(v)}
            />
        </div>

        <div style={{ marginTop: 12 }}>
            <div className={styles.legHead}>{t`Число поездок через парковку не менее:`}</div>
            <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                <Typography
                    gutterBottom
                    style={{ marginTop: 4 }}
                >
                    <strong>{chainStatsMinTotalTrips}</strong>
                </Typography>
                <Slider
                    value={CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS.indexOf(chainStatsMinTotalTrips)}
                    min={0}
                    max={CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS.length - 1}
                    step={1}
                    marks={CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS.map((_, idx) => ({ value: idx }))}
                    valueLabelDisplay="off"
                    onChange={(_, idx) => onChangeChainStatsMinTotalTrips(CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS[idx])}
                />
            </div>
        </div>

        <div style={{ marginTop: 12 }}>
            <div className={styles.legHead}>{t`Цвет парковок — число поездок`}:</div>
            <GradientLegend
                items={CHAIN_STATS_NODE_COLOR_ITEMS}
                renderConfig={CHAIN_STATS_GRADIENT_RENDER_CONFIG}
            />
        </div>

        {chainStatsSelectedParkingStats && (
            <div style={{ marginTop: 12 }}>
                <div className={styles.legHead}>{t`Открытая цепочка`}:</div>
                <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                    <Typography variant="body2">
                        {t`Число ребаланс-событий`}: <strong>{chainStatsSelectedParkingStats.chain_count}</strong>
                    </Typography>
                    <Typography variant="body2">
                        {t`Максимальная длина цепочки`}: <strong>{chainStatsSelectedParkingStats.max_chain_length}</strong>
                    </Typography>
                    <Typography variant="body2">
                        {t`Медианная длина цепочки`}: <strong>{chainStatsSelectedParkingStats.median_chain_length}</strong>
                    </Typography>
                </div>
                <div style={{ marginTop: 12 }}>
                    <div className={styles.legHead}>{t`Цвет рёбер — число поездок`}:</div>
                    <GradientLegend
                        items={CHAIN_STATS_EDGE_COLOR_ITEMS}
                        renderConfig={CHAIN_STATS_GRADIENT_RENDER_CONFIG}
                    />
                </div>
            </div>
        )}
    </div>
);
const mapStateToProps = ({
    mapFilters: {
        chainStatsMinCount,
        chainStatsSizeMetric,
        chainStatsMinTotalTrips,
        chainStatsSelectedParkingId, chainStatsSelectedParkingStats,
    },
}) => ({
    chainStatsMinCount,
    chainStatsSizeMetric,
    chainStatsMinTotalTrips,
    chainStatsSelectedParkingId,
    chainStatsSelectedParkingStats,
});
const ConnectedComponent = connect(
    mapStateToProps,
    { onChangeChainStatsMinCount, onChangeChainStatsSizeMetric, onChangeChainStatsMinTotalTrips, onClearChainStatsSelection }
)(ChainStatisticsLegendComponent);

export const ChainStatisticsLegend = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Статистика цепочек трипов`
);
